import { createFileRoute } from '@tanstack/react-router'
import { CheckIcon, CircleXIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react'

import Breadcrumbs from '@/containers/breadcrumbs'

import { Alert } from '@/components/alert'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { Card, CardContent } from '@/components/card'
import Content from '@/components/content'
import PageHeader from '@/components/pageHeader'
import { StatusCircle } from '@/components/statusCircle'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table'
import { ToastProps } from '@/components/toast'
import { toast } from '@/components/toast/useToast.ts'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'

import PlaygroundNavigation from '../-components/playgroundNavigation'

export const Route = createFileRoute('/_authed/$client_code/playground/components/')({
    component: PlaygroundComponents,
})

function PlaygroundComponents() {
    const { client_code } = Route.useParams()
    useSetBreadcrumbs([
        {
            to: '/$client_code/playground',
            text: 'Playground',
            params: { client_code },
        },
        'seperator',
        {
            to: '/$client_code/playground/components',
            text: 'Components',
            params: { client_code },
        },
    ])

    const showToast = (variant: ToastProps['variant']) => {
        return () => {
            toast({
                title: `This is a '${variant}' toast`,
                variant: variant,
            })
        }
    }

    return (
        <>
            <Breadcrumbs />
            <Content>
                <PageHeader title="Components" />
                <PlaygroundNavigation active="components" />

                <h3 className="my-4">Alerts</h3>
                <div className="mb-4">
                    <Alert className="flex items-center">
                        <div className="mr-4">
                            <InfoIcon />
                        </div>
                        <p>Default</p>
                    </Alert>
                    <Alert variant="success" className="flex items-center">
                        <div className="mr-4">
                            <CheckIcon />
                        </div>

                        <p>Success</p>
                    </Alert>
                    <Alert variant="warning" className="flex items-center">
                        <div className="mr-4">
                            <TriangleAlertIcon />
                        </div>

                        <p>Warning</p>
                    </Alert>
                    <Alert variant="destructive" className="flex items-center">
                        <div className="mr-4">
                            <CircleXIcon />
                        </div>
                        <p>Destructive</p>
                    </Alert>
                </div>

                <h3 className="my-4">Buttons</h3>
                <div className="mb-4">
                    <Button className="mr-4" onClick={showToast('default')}>
                        Default **
                    </Button>
                    <Button className="mr-4" variant="secondary">
                        Secondary
                    </Button>
                    <Button className="mr-4" variant="success" onClick={showToast('success')}>
                        Success **
                    </Button>
                    <Button className="mr-4" variant="warning" onClick={showToast('warning')}>
                        Warning **
                    </Button>
                    <Button className="mr-4" variant="destructive" onClick={showToast('destructive')}>
                        Destructive **
                    </Button>
                    <Button className="mr-4" variant="outline">
                        Outline
                    </Button>
                    <Button className="mr-4" variant="ghost">
                        Ghost
                    </Button>
                    <Button className="mr-4" variant="link">
                        Link
                    </Button>
                </div>

                <p>
                    ** also triggers a <strong>Toast</strong> notification
                </p>

                <h3 className="my-4">Row of cards</h3>
                <div className="mb-4 grid grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="pt-6">
                            <h3>SenderCompId</h3>
                            <h5>MOXIE-1</h5>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <h3>TargetCompId</h3>
                            <h5>MOXIE-TEST</h5>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <h3>BeginString</h3>
                            <h5>FIXT.1.1</h5>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <h3>DefaultAppVerId</h3>
                            <h5>FIX.5.0SP1</h5>
                        </CardContent>
                    </Card>
                </div>

                <h3 className="my-4">Badges</h3>
                <div className="mb-4">
                    <Badge className="mr-4">Default</Badge>
                    <Badge className="mr-4" variant="secondary">
                        Secondary
                    </Badge>
                    <Badge className="mr-4" variant="success">
                        Success
                    </Badge>
                    <Badge className="mr-4" variant="warning">
                        Warning
                    </Badge>
                    <Badge className="mr-4" variant="destructive">
                        Destructive
                    </Badge>
                    <Badge className="mr-4" variant="outline">
                        Outline
                    </Badge>
                </div>

                <h3 className="my-4">Status Circles</h3>
                <Card>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <StatusCircle />
                                </TableCell>
                                <TableCell>Default</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <StatusCircle variant="secondary" />
                                </TableCell>
                                <TableCell>Secondary</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <StatusCircle variant="success" />
                                </TableCell>
                                <TableCell>Success</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <StatusCircle variant="warning" />
                                </TableCell>
                                <TableCell>Warning</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <StatusCircle variant="destructive" />
                                </TableCell>
                                <TableCell>Destructive</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>
                                    <StatusCircle variant="outline" />
                                </TableCell>
                                <TableCell>Outline</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Card>

                <h3 className="my-4">Table</h3>
                <Card>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Code</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Timezone</TableHead>
                                <TableHead>State</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow>
                                <TableCell>MOXIE-1</TableCell>
                                <TableCell>FIX</TableCell>
                                <TableCell>MOXIE-1</TableCell>
                                <TableCell>Europe/Amsterdam</TableCell>
                                <TableCell>Active</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>MOXIE-2</TableCell>
                                <TableCell>FIX</TableCell>
                                <TableCell>MOXIE-2</TableCell>
                                <TableCell>Europe/Amsterdam</TableCell>
                                <TableCell>Active</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Card>
            </Content>
        </>
    )
}
